import { z } from 'zod';

import { BILL_STATUSES, INCOME_SOURCE_TYPES, RECURRENCE_RULES } from '../enums';
import {
  zCurrency,
  zDayOfMonth,
  zISODate,
  zId,
  zMoney,
  zNonEmptyString,
  zOptionalString,
  zPagination,
  zSort,
} from './common';

/* ─── Регулярные платежи ────────────────────────────────────────────────── */

const recurrenceFields = {
  rule: z.enum(RECURRENCE_RULES).default('MONTHLY'),
  intervalCount: z.number().int().min(1).max(60).default(1),
  dayOfMonth: zDayOfMonth.nullish(),
  weekday: z.number().int().min(0).max(6).nullish(),
  monthOfYear: z.number().int().min(1).max(12).nullish(),
  startDate: zISODate,
  endDate: zISODate.nullish(),
};

export const createRecurringSchema = z
  .object({
    name: zNonEmptyString(80),
    vendor: zOptionalString(80),
    categoryId: zId.nullish(),
    walletId: zId.nullish(),
    amountMinor: zMoney,
    currency: zCurrency,
    autoGenerateDaysAhead: z.number().int().min(7).max(180).default(45),
    notes: zOptionalString(500),
    ...recurrenceFields,
  })
  .refine((value) => !value.endDate || value.endDate >= value.startDate, {
    message: 'validation.endBeforeStart',
    path: ['endDate'],
  });
export type CreateRecurringInput = z.infer<typeof createRecurringSchema>;

export const updateRecurringSchema = z.object({
  name: zNonEmptyString(80).optional(),
  vendor: zOptionalString(80),
  categoryId: zId.nullish(),
  walletId: zId.nullish(),
  amountMinor: zMoney.optional(),
  rule: z.enum(RECURRENCE_RULES).optional(),
  intervalCount: z.number().int().min(1).max(60).optional(),
  dayOfMonth: zDayOfMonth.nullish(),
  weekday: z.number().int().min(0).max(6).nullish(),
  monthOfYear: z.number().int().min(1).max(12).nullish(),
  startDate: zISODate.optional(),
  endDate: zISODate.nullish(),
  isPaused: z.boolean().optional(),
  autoGenerateDaysAhead: z.number().int().min(7).max(180).optional(),
  notes: zOptionalString(500),
});
export type UpdateRecurringInput = z.infer<typeof updateRecurringSchema>;

/* ─── Счета ─────────────────────────────────────────────────────────────── */

export const payBillSchema = z.object({
  amountMinor: zMoney.optional(),
  walletId: zId,
  paidAt: zISODate,
  note: zOptionalString(300),
});
export type PayBillInput = z.infer<typeof payBillSchema>;

export const updateBillSchema = z.object({
  /** Разовое изменение суммы: правило остаётся прежним. */
  amountMinor: zMoney.optional(),
  dueDate: zISODate.optional(),
  walletId: zId.nullish(),
  categoryId: zId.nullish(),
  note: zOptionalString(300),
  status: z.enum(['SCHEDULED', 'SKIPPED', 'CANCELED']).optional(),
});
export type UpdateBillInput = z.infer<typeof updateBillSchema>;

export const billFilterSchema = zPagination.merge(zSort).extend({
  recurringId: zId.optional(),
  status: z.enum(BILL_STATUSES).optional(),
  from: zISODate.optional(),
  to: zISODate.optional(),
  preset: z.enum(['upcoming', 'overdue', 'paid', 'all']).optional(),
});
export type BillFilter = z.infer<typeof billFilterSchema>;

/* ─── Источники дохода (F-05) ───────────────────────────────────────────── */

export const createIncomeSourceSchema = z
  .object({
    name: zNonEmptyString(80),
    type: z.enum(INCOME_SOURCE_TYPES).default('SPONSOR'),
    contactPerson: zOptionalString(80),
    contactInfo: zOptionalString(200),
    amountMinor: zMoney,
    currency: zCurrency,
    walletId: zId.nullish(),
    notes: zOptionalString(500),
    ...recurrenceFields,
  })
  .refine((value) => !value.endDate || value.endDate >= value.startDate, {
    message: 'validation.endBeforeStart',
    path: ['endDate'],
  });
export type CreateIncomeSourceInput = z.infer<typeof createIncomeSourceSchema>;

export const updateIncomeSourceSchema = z.object({
  name: zNonEmptyString(80).optional(),
  type: z.enum(INCOME_SOURCE_TYPES).optional(),
  contactPerson: zOptionalString(80),
  contactInfo: zOptionalString(200),
  amountMinor: zMoney.optional(),
  walletId: zId.nullish(),
  rule: z.enum(RECURRENCE_RULES).optional(),
  intervalCount: z.number().int().min(1).max(24).optional(),
  dayOfMonth: zDayOfMonth.nullish(),
  weekday: z.number().int().min(0).max(6).nullish(),
  monthOfYear: z.number().int().min(1).max(12).nullish(),
  endDate: zISODate.nullish(),
  isPaused: z.boolean().optional(),
  notes: zOptionalString(500),
});
export type UpdateIncomeSourceInput = z.infer<typeof updateIncomeSourceSchema>;

export const receiveIncomeSchema = z.object({
  amountMinor: zMoney.optional(),
  walletId: zId,
  receivedAt: zISODate,
  note: zOptionalString(300),
});
export type ReceiveIncomeInput = z.infer<typeof receiveIncomeSchema>;

/* ─── Ответы ────────────────────────────────────────────────────────────── */

export interface RecurringDto {
  id: string;
  name: string;
  vendor: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categoryColor: string | null;
  walletId: string | null;
  walletName: string | null;

  amountMinor: number;
  currency: string;

  rule: string;
  intervalCount: number;
  dayOfMonth: number | null;
  weekday: number | null;
  monthOfYear: number | null;
  startDate: string;
  endDate: string | null;

  isPaused: boolean;
  autoGenerateDaysAhead: number;
  notes: string | null;

  /** Человекочитаемое описание расписания: «ежемесячно 5-го числа». */
  scheduleText: string;
  nextDueDate: string | null;
  /** Во сколько обходится в месяц — для сравнения подписок между собой. */
  monthlyCostMinor: number;

  /** Сколько счетов уже оплачено и на какую сумму. */
  paidCount: number;
  paidTotalMinor: number;
  createdAt: string;
}

/**
 * Одна оплата счёта. Счёт можно гасить частями, и каждая часть — отдельная
 * расходная операция: иначе отменить одну из двух оплат нельзя, не соврав
 * при этом об остальных.
 */
export interface BillPaymentRow {
  transactionId: string;
  amountMinor: number;
  paidAt: string;
  walletId: string;
  walletName: string | null;
}

export interface BillDto {
  id: string;
  recurringId: string | null;
  recurringName: string | null;
  name: string;
  amountMinor: number;
  currency: string;
  dueDate: string;
  status: string;
  paidMinor: number;
  remainderMinor: number;
  daysUntilDue: number;
  walletId: string | null;
  walletName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  paidAt: string | null;
  /** Все действующие оплаты, от свежей к старой. */
  payments: BillPaymentRow[];
  note: string | null;
}

export interface BillListResponse {
  items: BillDto[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  summary: {
    dueMinor: number;
    overdueMinor: number;
    overdueCount: number;
    paidMinor: number;
  };
}

export interface IncomeSourceDto {
  id: string;
  name: string;
  type: string;
  contactPerson: string | null;
  contactInfo: string | null;
  amountMinor: number;
  currency: string;
  walletId: string | null;
  walletName: string | null;
  scheduleText: string;
  nextDueDate: string | null;
  isPaused: boolean;
  notes: string | null;
  /** Сколько уже получено по этому источнику. */
  receivedTotalMinor: number;
  /** Ожидается к поступлению, но ещё не пришло. */
  pendingMinor: number;
  overdueMinor: number;
  /** Сколько поступлений просрочено — «нам должны» надо видеть числом. */
  overdueCount: number;
}

export interface IncomeInvoiceDto {
  id: string;
  incomeSourceId: string | null;
  sourceName: string | null;
  name: string;
  amountMinor: number;
  currency: string;
  dueDate: string;
  status: string;
  receivedMinor: number;
  walletId: string | null;
  walletName: string | null;
  receivedAt: string | null;
  note: string | null;
  isOverdue: boolean;
}

export interface IncomeOverview {
  sources: IncomeSourceDto[];
  upcoming: IncomeInvoiceDto[];
  overdue: IncomeInvoiceDto[];
  currency: string;
  /** Ожидаемые поступления за ближайшие 30 дней в базовой валюте. */
  expectedMinor: number;
  overdueMinor: number;
}

/** Единый элемент «ближайших платежей»: и людям, и по счетам. */
export interface UpcomingItemDto {
  kind: 'PAYOUT' | 'BILL' | 'INCOME';
  id: string;
  title: string;
  subtitle: string | null;
  amountMinor: number;
  currency: string;
  dueDate: string;
  daysUntilDue: number;
  status: string;
  isOverdue: boolean;
  personId: string | null;
  walletId: string | null;
}
